import { OUTPUT_FORMATS } from "~/constants/EditorTypes";
import extractSubject from "./extractSubject";
import inlineStyles from "./inlineStyles";
import parseOutputSegments from "./parseOutputSegments";

// Injects the user's saved signature into the AI output.
// Rich HTML: the AI reserves a <div class="brokerkit-signature-slot"></div>
// placeholder — we swap it for the real signature. Otherwise (plain text
// converted to <p>, etc.) we append at the top level.
const appendSignature = (html, signature) => {
  if (!signature) {
    return html;
  }
  const signatureHtml = `<div class="brokerkit-signature" style="margin-top: 24px;">${signature}</div>`;

  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;
  const slot = wrapper.querySelector(".brokerkit-signature-slot");
  if (slot) {
    slot.outerHTML = signatureHtml;
    return wrapper.innerHTML;
  }

  return `${html}${signatureHtml}`;
};

/**
 * Parse AI output, extract subject/body, and insert into the editor.
 */
const insertContent = (
  outputText,
  markdownContentRef,
  editor,
  mode,
  setSubject,
  outputFormat,
  skipSignature = false,
) => {
  const isTextFormat = outputFormat === OUTPUT_FORMATS.TEXT;

  // Text format: convert newlines to <br> / <p> so HTML editors preserve spacing
  if (isTextFormat) {
    let subject = null;
    let text = outputText || "";

    const subjectMatch = text.match(/^Subject:\s*(.+)/m);
    if (subjectMatch) {
      subject = subjectMatch[1].trim();
      text = text.replace(/^Subject:\s*.+\n?/, "").trimStart();
    }

    if (mode === "html") {
      // Wrap each paragraph (double newline separated) in <p>, single newlines become <br>
      const paragraphs = text.split(/\n{2,}/);
      let html = paragraphs
        .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
        .join("");

      // Append user signature if available (BK-3229: skipped for call scripts)
      const userSignature =
        typeof Rails !== "undefined" && Rails.helpers?.currentUser?.signature;
      if (!skipSignature) {
        html = appendSignature(html, userSignature);
      }

      editor.setContent(html, { format: "html" });
    } else {
      editor.setContent(text, { format: "text" });
    }

    if (subject && setSubject) {
      setSubject(subject);
    }
    return;
  }

  // HTML format: parse segments and handle HTML preview / markdown rendering
  const segments = parseOutputSegments(outputText);
  const htmlSegment = segments.find((s) => s.type === "html-preview");

  let content;
  let subject = null;

  if (htmlSegment) {
    const inlined = inlineStyles(htmlSegment.content);
    ({ content, subject } = extractSubject(inlined, "Subject: "));
  } else {
    content = markdownContentRef.current?.innerHTML || "";
  }

  // Check markdown segments for subject line
  if (!subject) {
    for (const seg of segments) {
      if (seg.type === "markdown") {
        const match = seg.content.match(/^Subject:\s*(.+)/m);
        if (match) {
          subject = match[1].trim();
          break;
        }
      }
    }
  }

  // Remove subject line from markdown body to avoid duplication
  if (!htmlSegment && subject && content) {
    const temp = document.createElement("div");
    temp.innerHTML = content;
    const first = temp.firstElementChild;
    if (first && first.textContent.trim().startsWith("Subject:")) {
      first.remove();
      const spacer = temp.firstElementChild;
      if (
        spacer &&
        (spacer.innerHTML === "&nbsp;" || spacer.textContent === "\u00a0")
      ) {
        spacer.remove();
      }
      content = temp.innerHTML;
    }
  }

  // Append user signature if available (HTML mode only; BK-3229: skipped for
  // call scripts, which are spoken on a call rather than signed like an email)
  const userSignature =
    typeof Rails !== "undefined" && Rails.helpers?.currentUser?.signature;
  if (userSignature && mode === "html" && !skipSignature) {
    content = appendSignature(content, userSignature);
  }

  editor.setContent(content, { format: mode === "html" ? "html" : "text" });

  if (subject && setSubject) {
    setSubject(subject);
  }
};

export default insertContent;
